// Story 7.2 AC5 — Lista de entitlements da subscription com snapshot completo.
// Mostra is_custom flag (badge) + limits snapshot (key:value pills).

import { Layers, Package2, Sparkles } from 'lucide-react';

import { cn } from '@control-center/ui';

import type { SubscriptionDetail } from '../../lib/subscriptions-api';

interface EntitlementsListProps {
  entitlements: SubscriptionDetail['entitlements'];
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
  );
}

function formatLimitValue(value: unknown): string {
  if (typeof value === 'number') return value.toLocaleString('pt-BR');
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  if (value === null) return '—';
  return JSON.stringify(value);
}

export function EntitlementsList({ entitlements }: EntitlementsListProps): JSX.Element {
  return (
    <section className="rounded-[14px] border border-line bg-panel p-5">
      <div className="mb-3 flex items-center gap-2">
        <Layers size={14} strokeWidth={1.8} className="text-muted" />
        <h3 className="m-0 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Entitlements ({entitlements.length})
        </h3>
      </div>
      {entitlements.length === 0 ? (
        <p className="text-[12px] text-muted">Sem entitlements (service-only).</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {entitlements.map((e) => {
            const limits = e.limitsSnapshot;
            const limitsRecord = isPlainRecord(limits) ? limits : null;
            return (
              <li
                key={e.id}
                className="flex items-start gap-3 rounded-[8px] border border-line bg-panel-2 p-3"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
                  <Package2 size={14} strokeWidth={1.8} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-medium text-ink">
                      {e.saasProduct.name} → {e.externalPlanName}
                    </div>
                    {e.isCustom && (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-[6px] border border-warn/40 bg-warn/10 px-2 py-[1px]',
                          'text-[10px] font-medium uppercase tracking-wider text-warn',
                        )}
                        title="Snapshot foi editado pós-criação"
                      >
                        <Sparkles size={9} strokeWidth={2} />
                        custom
                      </span>
                    )}
                  </div>
                  <code className="text-[11px] text-muted-2">
                    {e.saasProduct.slug}/{e.externalPlanSlug}
                  </code>
                  {limitsRecord && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(limitsRecord).map(([k, v]) => (
                        <span
                          key={k}
                          className="inline-flex items-center gap-1 rounded-[6px] border border-line bg-panel-3 px-2 py-[1px] font-mono text-[10px] text-muted"
                        >
                          <span className="text-muted-3">{k}:</span>
                          <span className="text-ink">{formatLimitValue(v)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
